import React, { useState } from 'react';
import { MessageSquarePlus, Bug, Lightbulb, Star, Send, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { feedbackApi } from '../lib/api';
import { toast } from 'sonner';

const BetaFeedback = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('general');
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const feedbackTypes = [
    { id: 'bug', icon: Bug, label: 'Bug Report', color: 'text-red-400' },
    { id: 'feature', icon: Lightbulb, label: 'Feature Request', color: 'text-amber-400' },
    { id: 'improvement', icon: Star, label: 'Improvement', color: 'text-blue-400' },
    { id: 'general', icon: MessageSquarePlus, label: 'General', color: 'text-emerald-400' },
  ];

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    setSubmitting(true);
    try {
      await feedbackApi.submit({
        feedback_type: feedbackType,
        rating: rating > 0 ? rating : null,
        message: message.trim(),
        page_url: window.location.href
      });
      toast.success('Thank you for your feedback!');
      setMessage('');
      setRating(0);
      setFeedbackType('general');
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all z-50 group"
        data-testid="beta-feedback-btn"
      >
        <MessageSquarePlus className="w-6 h-6" />
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-sm px-3 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          Beta Feedback
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 bg-white dark:bg-[#111827] rounded-2xl shadow-2xl border border-slate-200 dark:border-[#1F2937] z-50 overflow-hidden" data-testid="beta-feedback-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] p-4 flex items-center justify-between">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <MessageSquarePlus className="w-5 h-5" />
          Beta Feedback
        </h3>
        <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Feedback Type */}
        <div>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">
            What type of feedback?
          </label>
          <div className="grid grid-cols-2 gap-2">
            {feedbackTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setFeedbackType(type.id)}
                className={`flex items-center gap-2 p-3 rounded-xl border transition-all ${
                  feedbackType === type.id
                    ? 'border-[#26C6DA] bg-[#26C6DA]/10'
                    : 'border-slate-200 dark:border-slate-700 hover:border-[#26C6DA]/50'
                }`}
              >
                <type.icon className={`w-4 h-4 ${type.color}`} />
                <span className="text-sm text-slate-700 dark:text-slate-300">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Rating */}
        <div>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">
            How's your experience so far? (optional)
          </label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className={`p-1 transition-colors ${
                  star <= rating ? 'text-amber-400' : 'text-slate-300 dark:text-slate-600'
                }`}
              >
                <Star className={`w-6 h-6 ${star <= rating ? 'fill-current' : ''}`} />
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">
            Your feedback
          </label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              feedbackType === 'bug' ? "Describe the bug and steps to reproduce..." :
              feedbackType === 'feature' ? "What feature would you like to see?" :
              feedbackType === 'improvement' ? "What could be improved?" :
              "Share your thoughts..."
            }
            className="min-h-[100px] bg-slate-50 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-200"
            data-testid="feedback-message"
          />
        </div>

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={submitting || !message.trim()}
          className="w-full bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-white rounded-full"
          data-testid="submit-feedback-btn"
        >
          {submitting ? (
            'Sending...'
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Send Feedback
            </>
          )}
        </Button>

        <p className="text-xs text-center text-slate-500 dark:text-slate-400">
          Your feedback helps us improve Strategic Architect
        </p>
      </div>
    </div>
  );
};

export default BetaFeedback;
